import { CmsValue } from 'core/type/BootstrapConfig.types';
import { createSelector } from '@reduxjs/toolkit';
import { selectCmsFromBootstrap, selectExperimentsFromBootstrap, selectLocaleFromBootstrap } from 'store/slice/bootstrap';
import {
    selectAllOfferOptions,
    selectFinalOfferOption,
    selectOfferLevelSelectedDate,
    selectOfferSelected,
    selectPassengerOption,
    selectSelectedBookingRequirement,
    selectSelectedSpecifications,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { EnrichedBookingRequirement, SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { selectActivityDetail, selectActivityImagesFromDetailState } from 'component/page/detail/store/detail.selectors';
import { Pax } from 'gql/autogen/Pax';
import { Image } from 'gql/autogen/Image';
import { getFormattedDate } from 'core-framework/util/datetime/date';
import { getOrderedKeysSpecifications, TIME_KEY } from 'component/page/detail/PackageOptions/ExpandedOffer/OfferOptions/OfferOptionsUtils';
import { Specification } from 'gql/autogen/Specification';
import { getAppliedPromocodeItem, getOfferGroupDetailByRef } from 'component/page/detail/detailUtils';
import { SummaryElement } from 'gql/autogen/SummaryElement';
import { DisplayPromocodeItem } from 'gql/autogen/DisplayPromocodeItem';
import { navigateToHashWithTabBarOffset } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { setIsScrolling } from 'component/page/detail/store/details';
import { NavigationHashEnum } from 'core/type/Details.types';
import { selectTabs } from 'component/page/detail/DetailPageTabs/DetailPageTabs.selectors';
import { isOn } from 'core/window/bootstrapConfig';
import { FeatureName } from 'core/constant/FeatureName';
import { PACKAGE_OPTIONS_HREF } from 'component/page/detail/DetailPageModularize/contentRegistry.type';

interface StateProps {
    cms: CmsValue;
    selectedOffer: SelectedOfferState;
    pax: Pax[];
    bookingRequirement: EnrichedBookingRequirement;
    image: Image | undefined;
    locale: string;
    dateTimeText: string;
    offerGroupTitle: string;
    pricePerPax: SummaryElement[];
    currency: string | undefined;
    sortedSelectedSpecifications: Specification[];
    appliedPromocodeItem: DisplayPromocodeItem;
    packageOptionsHref: string;
}

interface DispatchProps {
    onEditOfferClick: (packageOptionsHref: string) => void;
}

export type SelectedOfferSummaryCardProps = DispatchProps & StateProps;

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    onEditOfferClick: (packageOptionsHref: string) => {
        navigateToHashWithTabBarOffset(dispatch, `#${packageOptionsHref}`, setIsScrolling, true);
    },
});

export const mapStateToProps = createSelector(
    selectCmsFromBootstrap,
    selectOfferSelected,
    selectOfferLevelSelectedDate,
    selectActivityDetail,
    selectFinalOfferOption,
    selectPassengerOption,
    selectSelectedBookingRequirement,
    selectActivityImagesFromDetailState,
    selectLocaleFromBootstrap,
    selectSelectedSpecifications,
    selectAllOfferOptions,
    selectExperimentsFromBootstrap,
    selectTabs,
    (
        cms,
        selectedOffer,
        selectedDate,
        detail,
        finalOfferOption,
        pax,
        bookingRequirement,
        images,
        locale,
        selectedSpecifications,
        allOfferOptions,
        experiments,
        tabsObject,
    ): StateProps => {
        const dateText = getFormattedDate(selectedDate, cms, true, false);
        const nonTimeSpecifications = selectedSpecifications?.filter((spec) => spec.key !== TIME_KEY) ?? [];
        const timeSpecification = selectedSpecifications?.find((spec) => spec.key === TIME_KEY);
        const image = images.length > 0 ? images[0] : undefined;
        const pricing = finalOfferOption?.pricingSummary?.pricing?.[0];
        const { offer } = selectedOffer;
        const offerGroupDetail = offer ? getOfferGroupDetailByRef(detail, offer.offerGroupDetailRef) : undefined;
        const offerGroupTitle = offerGroupDetail?.offerGroupTitle ?? '';
        const pricePerPax = pricing?.display?.perPax || [];
        const currency = pricing?.currency;
        const allSpecificationKeys = getOrderedKeysSpecifications(allOfferOptions);
        const sortedSelectedSpecifications = [...nonTimeSpecifications].sort((a, b) => {
            const aIndex = allSpecificationKeys.indexOf(a.key);
            const bIndex = allSpecificationKeys.indexOf(b.key);
            return aIndex - bIndex;
        });
        const sortedSelectedSpecificationsWithTime = timeSpecification ? [...sortedSelectedSpecifications, timeSpecification] : sortedSelectedSpecifications;
        const appliedPromocodeItem = pricing ? getAppliedPromocodeItem(pricing, experiments) : {};
        const { tabs } = tabsObject;
        const packageOptionsTab = tabs.find((tab) => tab.id === NavigationHashEnum.PackageOptionsSection) || { href: '' };
        const isDetailPageForSimCardEnabled = isOn(FeatureName.DetailPageForSimCard);

        return {
            cms,
            selectedOffer,
            pax,
            bookingRequirement,
            image,
            locale,
            offerGroupTitle,
            pricePerPax,
            currency,
            appliedPromocodeItem,
            dateTimeText: dateText,
            sortedSelectedSpecifications: sortedSelectedSpecificationsWithTime,
            packageOptionsHref: isDetailPageForSimCardEnabled ? PACKAGE_OPTIONS_HREF : packageOptionsTab.href,
        };
    },
);
